import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["police_reports", "police_departments"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      policeReportsList: build.query<
        PoliceReportsListApiResponse,
        PoliceReportsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/`,
          params: {
            include_history: queryArg.includeHistory,
          },
        }),
        providesTags: ["police_reports"],
      }),
      policeReportsCreate: build.mutation<
        PoliceReportsCreateApiResponse,
        PoliceReportsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/`,
          method: "POST",
          body: queryArg.createPoliceReport,
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsRetrieve: build.query<
        PoliceReportsRetrieveApiResponse,
        PoliceReportsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/`,
        }),
        providesTags: ["police_reports"],
      }),
      policeReportsUpdate: build.mutation<
        PoliceReportsUpdateApiResponse,
        PoliceReportsUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.updatePoliceReport,
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsPartialUpdate: build.mutation<
        PoliceReportsPartialUpdateApiResponse,
        PoliceReportsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdatePoliceReport,
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsDestroy: build.mutation<
        PoliceReportsDestroyApiResponse,
        PoliceReportsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsGetBrowserRuns: build.query<
        PoliceReportsGetBrowserRunsApiResponse,
        PoliceReportsGetBrowserRunsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/browser-agent/runs/`,
        }),
        providesTags: ["police_reports"],
      }),
      policeReportsStartBrowserAgent: build.mutation<
        PoliceReportsStartBrowserAgentApiResponse,
        PoliceReportsStartBrowserAgentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/browser-agent/start/`,
          method: "POST",
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeDepartmentsListBrowserRuns: build.query<
        PoliceDepartmentsListBrowserRunsApiResponse,
        PoliceDepartmentsListBrowserRunsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/browser-runs/`,
        }),
        providesTags: ["police_departments"],
      }),
      policeReportsEnsureTasks: build.mutation<
        PoliceReportsEnsureTasksApiResponse,
        PoliceReportsEnsureTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/ensure-tasks/`,
          method: "POST",
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeDepartmentsFireBrowserRun: build.mutation<
        PoliceDepartmentsFireBrowserRunApiResponse,
        PoliceDepartmentsFireBrowserRunApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/fire-browser-run/`,
          method: "POST",
          body: queryArg.fireBrowserRunRequest,
        }),
        invalidatesTags: ["police_departments"],
      }),
      policeReportsUpdateAttestationStatus: build.mutation<
        PoliceReportsUpdateAttestationStatusApiResponse,
        PoliceReportsUpdateAttestationStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/update_attestation_status/`,
          method: "POST",
          body: queryArg.updatePoliceReportAttestationStatus,
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsGetWithAutomationData: build.query<
        PoliceReportsGetWithAutomationDataApiResponse,
        PoliceReportsGetWithAutomationDataApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/${queryArg.id}/with-automation-data/`,
        }),
        providesTags: ["police_reports"],
      }),
      policeReportsDiscover: build.mutation<
        PoliceReportsDiscoverApiResponse,
        PoliceReportsDiscoverApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/discover/`,
          method: "POST",
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsMatterSnapshot: build.query<
        PoliceReportsMatterSnapshotApiResponse,
        PoliceReportsMatterSnapshotApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/matter-snapshot/`,
        }),
        providesTags: ["police_reports"],
      }),
      policeReportsTriggerOrchestration: build.mutation<
        PoliceReportsTriggerOrchestrationApiResponse,
        PoliceReportsTriggerOrchestrationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/orchestration/trigger/`,
          method: "POST",
        }),
        invalidatesTags: ["police_reports"],
      }),
      policeReportsGetOutreachSummary: build.query<
        PoliceReportsGetOutreachSummaryApiResponse,
        PoliceReportsGetOutreachSummaryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/police-reports/outreach/summary/`,
        }),
        providesTags: ["police_reports"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type PoliceReportsListApiResponse =
  /** status 200  */ DetailedPoliceReport[];
export type PoliceReportsListApiArg = {
  /** Include automation history (voice/browser runs). Default: false for performance. */
  includeHistory?: boolean;
  matterId: string;
};
export type PoliceReportsCreateApiResponse =
  /** status 201  */ ReadPoliceReport;
export type PoliceReportsCreateApiArg = {
  matterId: string;
  createPoliceReport: CreatePoliceReport;
};
export type PoliceReportsRetrieveApiResponse =
  /** status 200  */ ReadPoliceReport;
export type PoliceReportsRetrieveApiArg = {
  id: string;
  matterId: string;
};
export type PoliceReportsUpdateApiResponse =
  /** status 200  */ ReadPoliceReport;
export type PoliceReportsUpdateApiArg = {
  id: string;
  matterId: string;
  updatePoliceReport: UpdatePoliceReport;
};
export type PoliceReportsPartialUpdateApiResponse =
  /** status 200  */ ReadPoliceReport;
export type PoliceReportsPartialUpdateApiArg = {
  id: string;
  matterId: string;
  patchedUpdatePoliceReport: UpdatePoliceReport2;
};
export type PoliceReportsDestroyApiResponse = unknown;
export type PoliceReportsDestroyApiArg = {
  id: string;
  matterId: string;
};
export type PoliceReportsGetBrowserRunsApiResponse = /** status 200  */ {
  runs?: any;
  count?: number;
};
export type PoliceReportsGetBrowserRunsApiArg = {
  id: string;
  matterId: string;
};
export type PoliceReportsStartBrowserAgentApiResponse = /** status 201  */ {
  message?: string;
  browser_run_id?: string;
  workflow_id?: string;
  status?: string;
};
export type PoliceReportsStartBrowserAgentApiArg = {
  id: string;
  matterId: string;
};
export type PoliceDepartmentsListBrowserRunsApiResponse =
  /** status 200  */ ListBrowserRunsResponse;
export type PoliceDepartmentsListBrowserRunsApiArg = {
  id: string;
  matterId: string;
};
export type PoliceReportsEnsureTasksApiResponse = /** status 202  */ {
  status?: string;
};
export type PoliceReportsEnsureTasksApiArg = {
  id: string;
  matterId: string;
};
export type PoliceDepartmentsFireBrowserRunApiResponse =
  /** status 201  */ DispatchBrowserAgentResult;
export type PoliceDepartmentsFireBrowserRunApiArg = {
  id: string;
  matterId: string;
  fireBrowserRunRequest: FireBrowserRunRequest;
};
export type PoliceReportsUpdateAttestationStatusApiResponse =
  /** status 200  */ ReadPoliceReport;
export type PoliceReportsUpdateAttestationStatusApiArg = {
  id: string;
  matterId: string;
  updatePoliceReportAttestationStatus: UpdatePoliceReportAttestationStatus;
};
export type PoliceReportsGetWithAutomationDataApiResponse =
  /** status 200  */ ReadPoliceReportWithHistoryRead;
export type PoliceReportsGetWithAutomationDataApiArg = {
  id: string;
  matterId: string;
};
export type PoliceReportsDiscoverApiResponse = /** status 200  */ {
  message?: string;
};
export type PoliceReportsDiscoverApiArg = {
  matterId: string;
};
export type PoliceReportsMatterSnapshotApiResponse =
  /** status 200  */ MatterSnapshotRead;
export type PoliceReportsMatterSnapshotApiArg = {
  matterId: string;
};
export type PoliceReportsTriggerOrchestrationApiResponse = /** status 202  */ {
  message?: string;
  matter_id?: string;
};
export type PoliceReportsTriggerOrchestrationApiArg = {
  matterId: string;
};
export type PoliceReportsGetOutreachSummaryApiResponse = /** status 200  */ {
  summary?: object;
};
export type PoliceReportsGetOutreachSummaryApiArg = {
  matterId: string;
};
export type PoliceReportAttestationStatus =
  | "rejected_verified"
  | "unclear"
  | "inferred"
  | "attested_unverified"
  | "attested_verified"
  | "verified_received";
export type PoliceReportAvailabilityStatus =
  | "verified_ready"
  | "verified_pending"
  | "unknown"
  | "available"
  | "not_available";
export type OfficerDetail = {
  name: string;
  badge_number?: string | null;
  context?: string | null;
};
export type PoliceReportEscalationReason =
  | "cant_verify_attested_report"
  | "verified_ready_but_no_automation_available"
  | "verified_but_unclear_if_ready"
  | "found_on_portal_but_failed_to_retrieve";
export type ContactHandleType =
  | "PHONE"
  | "EMAIL"
  | "WEBSITE"
  | "FAX"
  | "OTHER"
  | "MAILING_ADDRESS";
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: (string | string) | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: (string | string) | null;
};
export type JurisdictionType =
  | "city"
  | "county"
  | "state"
  | "federal"
  | "tribal"
  | "special";
export type PoliceDepartmentEnrichmentStatus =
  | "not_enriched"
  | "enrichment_in_progress"
  | "enrichment_failed"
  | "enrichment_succeeded";
export type ServiceType =
  | "crash_reports"
  | "foia_equivalent"
  | "criminal_records"
  | "incident_reports"
  | "general_records";
export type PoliceRecordsInquiryMajorVendor =
  | "carfax_crashdocs"
  | "lexis_nexis_buycrash"
  | "independent_portal"
  | "next_request"
  | "gov_qa"
  | "not_a_portal"
  | "txdot_cris";
export type AddressSchema = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  postal_or_zip?: string | null;
  country_code?: string | null;
};
export type MailRequirement =
  | "CLIENT_PHOTO_ID_COPY"
  | "FIRM_LETTERHEAD"
  | "PROOF_OF_REPRESENTATION"
  | "COMPLETED_REQUEST_FORM"
  | "NOTARIZED_REQUEST_FORM";
export type FeeComponent = {
  /** The amount of the fee in cents. */
  amount_cents: number;
  /** One line description of the component of the total fee backed by research and assumptions.
    ex:
    - "Upper bound estimate of 20 pages at $0.25/page as specified by ____"
    - "Estimating up to 30min of labor at stated rate of $20/hour as specified by ____"
    - "Base certified crash report fee $8.44 as specified by ____"
    - "Added 7% for tax because site mentions it's not included in provided info" */
  description: string;
};
export type RecordsFeeEstimate = {
  /** An estimated fee for requesting a copy of a police report.
    Depending on the department, sometimes this is called a "crash report", "accident report",
    "incident report", or "police report".
    
    Rule: IF more than one report option is available, bias to the more expensive, or certified option.
    If fees are not defined for the department, fall back to county or state guidance on fees. */
  estimated_fee_cents?: number | null;
  /** All the components that led to the estimated fee */
  components?: FeeComponent[] | null;
  /** Full verbatim fee details including:
    - Exact language from official source
    - Conditions, alternatives, or special cases
    - Statutory citations (e.g., "per RSMo §610.026")
    - Payment instructions
    ALWAYS populate this when ANY fee information is available. */
  notes?: string | null;
  /** A 0-1 score indicating the confidence. */
  confidence_score?: number | null;
  /** 2-3 sentences explaining the confidence score. */
  reasoning?: string | null;
};
export type CheckMailingInstructions = {
  /** Verifiable instructions on how to request a police report from the agency by mail. */
  instructions?: string;
  /** The mailing address to send the request to. */
  address: AddressSchema;
  /** The best, single name of the entity to make the check payable to. (Payee)
    Do not place anything except for a single, bank-ready name here. */
  payable_to?: string | null;
  /** Any specific text required to be written on the check memo line, if explicitly mentioned, otherwise null.
    Example: "Police Report Request" or "Report #[REPORT_NUMBER]" or "Accident Report Fee"
    available variables: "[REPORT_NUMBER], [CLIENT_NAME], [DATE_OF_LOSS]" */
  memo_instructions?: string | null;
  /** The number of days after incident date to wait before requesting
    the records by mail, if explicitly specified, otherwise null. */
  wait_before_requesting_days?: number | null;
  /** Any department-specified, or state-specified, number of days to expect for processing, if explicitly stated, otherwise null. */
  estimated_processing_days?: number | null;
  /** Any items that are explicitly REQUIRED for requesting a police report by mail, if described, otherwise null. */
  required_items?: MailRequirement[] | null;
  /** Any special requirements for requesting a police report by mail, if described, otherwise null. */
  special_requirements?: string | null;
  /** The estimated set of fees to request a copy of a police report by mail.
    
    - (for any question of the report type being sought): assume we want the most detailed version of the report, sometimes this is "crash report", "accident report", "incident report", "police report", bias to the more expensive of the four
    - (for any page-based fee structure) assume a 20 page incident report
    - (for any requestor-based fee differences) assume legal representative of involved party
    - (for request-method based pricing) assume check + mail but bias to more expensive option
    - (for any charge for a missing report number) assume we do not have the report number
    - (for any charges related to labor) assume 30 minutes of labor
    - (for any charges related to certified copies) assume we are paying for the most certified copy */
  fee_structure?: RecordsFeeEstimate;
  /** Web pages that were used to gather the information. */
  citations?: WebSource[] | null;
};
export type DepartmentForm = {
  /** Concise title for the Form */
  title: string;
  /** Concise sentence description of the Form and its purpose. */
  description?: string | null;
  /** URL of the Form file */
  file_url: string;
};
export type ReadDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  handle: ContactHandleSchema;
  fee_amount?: number | null;
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department_id: string | string;
  required_document_file_url?: string | null;
};
export type TimeZone = "PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST";
export type ReadPoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  records_channels?: ReadDepartmentRecordsChannel[];
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type SuggestionStatus =
  | "pending"
  | "applied"
  | "partially_applied"
  | "rejected";
export type SuggestionProvenanceStatus = "consistent" | "compromised";
export type SuggestionAction = "add_new" | "patch_existing" | "delete";
export type ExtractionType =
  | "police_report"
  | "person"
  | "organization"
  | "vehicle"
  | "property"
  | "animal"
  | "personal_relationship"
  | "vehicle_occupancy"
  | "ownership"
  | "employment";
export type ReadExtractionCitation = {
  /** The id of the artifact/file that the citation is from. */
  artifact_id?: string | null;
  /** If there's a "citation-id" block in the text
    that corresponds to the id, reference it here. */
  citation_id?: string | null;
  /** A verbatim snippet from the text that supports the extraction.
    Include enough pre/post context to be self-explanatory. */
  excerpt?: string | null;
  extraction_id: string | string;
  block_id?: (string | string) | null;
  transcript_line_id?: (string | string) | null;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type ReadArtifactExtraction = {
  extraction_type: ExtractionType;
  extraction: any;
  extra?: any | null;
  citations: ReadExtractionCitation[];
  id: string | string;
  artifact_id: (string | string) | null;
  created_at: string;
  modified_at: string;
};
export type ReadSuggestion = {
  status: SuggestionStatus;
  provenance_status: SuggestionProvenanceStatus;
  action: SuggestionAction;
  body?: any | null;
  extraction_ids: (string | string)[];
  extra: any;
  /** The component mention IDs that should be linked to the suggestion. */
  target_id?: (string | string) | null;
  id: string | string;
  created_at: string;
  modified_at: string;
  extractions: ReadArtifactExtraction[];
};
export type PostGridLetterSize = "us_letter" | "us_legal" | "a4";
export type PostGridAddressPlacement =
  | "top_first_page"
  | "top_of_page"
  | "insert_blank_page";
export type PostGridMailingClass =
  | "first_class"
  | "standard_class"
  | "express"
  | "certified"
  | "certified_return_receipt"
  | "registered"
  | "usps_first_class"
  | "usps_standard_class"
  | "usps_eddm"
  | "usps_express_2_day"
  | "usps_express_3_day"
  | "usps_first_class_certified"
  | "usps_first_class_certified_return_receipt"
  | "usps_first_class_registered"
  | "usps_express_3_day_signature_confirmation"
  | "usps_express_3_day_certified"
  | "usps_express_3_day_certified_return_receipt"
  | "ca_post_lettermail"
  | "ca_post_personalized"
  | "ca_post_neighbourhood_mail"
  | "ca_post_registered"
  | "ca_post_expedited_with_signature"
  | "ca_post_expedited"
  | "ca_post_xpresspost_with_signature"
  | "ca_post_xpresspost"
  | "royal_mail_first_class"
  | "royal_mail_standard_class"
  | "au_post_regular"
  | "au_post_second_class"
  | "ups_express_overnight"
  | "ups_express_2_day"
  | "ups_express_3_day"
  | "ups_express_3_day_signature_confirmation"
  | "ups_express_3_day_adult_signature_confirmation";
export type PostGridObjectStatus =
  | "creating"
  | "ready"
  | "printing"
  | "in_transit"
  | "in_local_area"
  | "processed_for_delivery"
  | "processed"
  | "delivered"
  | "re-routed"
  | "returned_to_sender"
  | "cancelled"
  | "completed"
  | "failed";
export type CarrierTrackingLocation = {
  city?: string | null;
  provinceOrState?: string | null;
  countryCode?: string | null;
  postalOrZip?: string | null;
};
export type CarrierTrackingUpdate = {
  message: string;
  status: string;
  createdAt: string;
  source: string;
  trackingLocation?: CarrierTrackingLocation;
};
export type CarrierTrackingData = {
  status: string;
  statusDetail?: string | null;
  estDeliveryDate?: string | null;
  signedBy?: string | null;
  carrier: string;
  trackingUpdates?: CarrierTrackingUpdate[];
};
export type ReadPostgridLetter = {
  subject?: string | null;
  content?: string | null;
  letter_size?: PostGridLetterSize;
  address_placement?: PostGridAddressPlacement;
  mailing_class?: PostGridMailingClass;
  color?: boolean;
  double_sided?: boolean;
  to_contact_id: string | string;
  from_contact_id?: (string | string) | null;
  return_envelope_id?: (string | string) | null;
  use_return_envelope?: boolean;
  live?: boolean;
  final_pdf_url?: string | null;
  matter_id: string | string;
  id: string | string;
  postgrid_id: string;
  status?: PostGridObjectStatus;
  created_at?: string | null;
  modified_at?: string | null;
  is_test?: boolean;
  tracking_number?: string | null;
  carrier_tracking?: CarrierTrackingData;
  imb_status?: string | null;
  imb_date?: string | null;
  imb_zip_code?: string | null;
  expected_delivery_date?: string | null;
  date_sent?: string | null;
  date_delivered?: string | null;
};
export type PostGridChequeType = "standard" | "voucher";
export type PostGridChequeSize = "us_letter" | "us_legal";
export type ReadPostgridCheque = {
  /** Cheque amount (min $0.01, max enforced by domain) */
  amount: string;
  memo?: string | null;
  number?: number | null;
  date_cleared?: string | null;
  cheque_type?: PostGridChequeType;
  cheque_size?: PostGridChequeSize;
  mailing_class?: PostGridMailingClass;
  to_contact_id: string | string;
  from_contact_id?: (string | string) | null;
  return_envelope_id?: (string | string) | null;
  use_return_envelope?: boolean;
  bank_account_id: (string | string) | null;
  live?: boolean;
  final_pdf_url?: string | null;
  id: string | string;
  matter_id: string | string;
  postgrid_id: string;
  status?: PostGridObjectStatus;
  created_at?: string | null;
  modified_at?: string | null;
  is_test?: boolean;
  tracking_number?: string | null;
  carrier_tracking?: CarrierTrackingData;
  imb_status?: string | null;
  imb_date?: string | null;
  imb_zip_code?: string | null;
  expected_delivery_date?: string | null;
  date_sent?: string | null;
  date_delivered?: string | null;
};
export type DetailedPoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
  suggestions?: ReadSuggestion[] | null;
  /** Letters sent to this police department for this matter */
  sent_letters?: ReadPostgridLetter[];
  /** Cheques sent to this police department for this matter */
  sent_cheques?: ReadPostgridCheque[];
};
export type ReadPoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
};
export type CreatePoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id: string | string;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
};
export type UpdatePoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  id: string | string;
  officers?: OfficerDetail[] | null;
};
export type UpdatePoliceReport2 = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  id: string | string;
  officers?: OfficerDetail[] | null;
};
export type BrowserAgentRunStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "failed"
  | "cancelled";
export type ReadBrowserAgentRun = {
  /** Current status of the browser automation run */
  status: BrowserAgentRunStatus;
  /** Temporal workflow ID if applicable */
  workflow_id?: string | null;
  /** When this run is scheduled to execute */
  scheduled_for?: string | null;
  /** When the run actually started */
  started_at?: string | null;
  /** When the run completed */
  completed_at?: string | null;
  /** Vendor portal used for this run (e.g., LEXIS_NEXIS_BUYCRASH) */
  portal_vendor?: PoliceRecordsInquiryMajorVendor;
  /** Input payload for portal request/details lookup (PoliceReportData) */
  request_payload?: {
    [key: string]: any;
  } | null;
  /** How many attempts have been made */
  attempt_number?: number;
  /** Whether a report was found during the run */
  found_report?: boolean;
  /** Whether the report is ready for retrieval */
  report_ready?: boolean;
  /** URL where the report was found/retrieved */
  report_url?: string | null;
  /** ETA for report availability, if provided by portal */
  report_eta?: string | null;
  /** Additional result data from the browser automation */
  result_data?: {
    [key: string]: any;
  };
  /** Error message from the browser automation */
  error_message?: string | null;
  /** URL of the purchased file, if any */
  purchased_file?: string | null;
  id: string | string;
  police_report_id: string | string;
  created_at: string;
  modified_at: string;
};
export type ListBrowserRunsResponse = {
  runs: ReadBrowserAgentRun[];
  count: number;
};
export type DispatchBrowserAgentResult = {
  browser_run_id: string;
  status: BrowserAgentRunStatus;
  workflow_id?: string | null;
};
export type FireBrowserRunRequest = {
  police_report_id: string;
};
export type UpdatePoliceReportAttestationStatus = {
  /** New attestation status for the police report */
  attestation_status: PoliceReportAttestationStatus;
};
export type VoiceAgentStatus =
  | "started"
  | "scheduled"
  | "thinking"
  | "dev_stalled"
  | "call_in_progress"
  | "waiting"
  | "cancelled"
  | "completed"
  | "ineligible"
  | "waiting_info"
  | "failed";
export type NormalizedEvaluationSchema = {
  /** Normalized success status. Values: 'success', 'full_success', 'partial_success', 'no_success', 'failure', 'uncontrollable', 'unclear' */
  success_status: string;
  /** Summary of the call outcome (from 'summary' or 'call_summary') */
  call_summary?: string;
  /** Model confidence score (0-1), present for claims evaluations */
  confidence?: number | null;
  /** Granular success score (0-1), present for claims evaluations */
  score?: number | null;
  /** Information items captured during call (claims) */
  info?:
    | {
        [key: string]: string | null;
      }[]
    | null;
  /** Issues encountered during call (claims) */
  issues?: string[] | null;
  /** Guidelines violated during call (claims) */
  violated_guidelines?: string[] | null;
  /** Recommended follow-up action (claims) */
  follow_up?: string | null;
  /** Relevant quotes from transcript (claims) */
  quotes?: string[] | null;
  /** Submission methods mentioned during call (medical records) */
  alternative_submission_methods?: string[] | null;
  /** Alternate contact methods discovered (medical records) */
  alternates?:
    | {
        [key: string]: any;
      }[]
    | null;
  /** Action items from call (medical records) */
  action_items?: string[] | null;
  /** Whether to resend to same contact (medical records) */
  resend_same_contact?: boolean | null;
  /** Reason for resending (medical records) */
  resend_same_contact_reason?: string | null;
  /** Treatment record request status (medical records) */
  request_status?: string | null;
  /** Slack notification category (medical records) */
  notification_category?: string | null;
  /** Slack notification subcategory (medical records) */
  notification_subcategory?: string | null;
  /** Key information extracted from call (medical records) */
  key_information?: {
    [key: string]: string | null;
  } | null;
  /** Whether call reached a human (medical records) */
  reached_human?: boolean | null;
};
export type CallResultSchema = {
  id: string | string;
  call_id: string;
  workflow_id?: string | null;
  phone_number?: string | null;
  status: string;
  created_at?: string | null;
  started_at?: string | null;
  transcript?: string | null;
  transcript_available?: boolean;
  summary?: string | null;
  duration?: number | null;
  duration_seconds?: number | null;
  error?: string | null;
  error_message?: string | null;
  evaluation?: NormalizedEvaluationSchema;
  evaluation_available?: boolean;
};
export type SimpleReadinessInfo = {
  /** Variables that are missing values */
  missing_items?: string[];
  /** Friendly summary for UI (e.g. 'policy_number +2 more') */
  summary?: string;
};
export type TaskSummarySchema = {
  /** Unique task identifier */
  task_id: string;
  /** Canonical task group (e.g., 'Police Report') */
  group_canonical: string;
  /** Display name of task group */
  group_name: string;
  /** Matter/claimant name */
  matter_name: string;
  /** Related entity (police dept, carrier, etc.) */
  entity_name?: string | null;
  /** Task creation date (ISO string) */
  created_at: string;
  /** Task due date (ISO string) */
  due_date?: string | null;
};
export type VoiceAiAgentRunSchema = {
  id: string | string;
  workflow_id: string;
  task_id: string;
  status: VoiceAgentStatus;
  scheduled_for?: string | null;
  calls_made: number;
  max_calls_allowed: number;
  summary?: string | null;
  last_message?: string | null;
  event_log?: {
    [key: string]: string;
  }[];
  messages?:
    | {
        [key: string]: any;
      }[]
    | null;
  error_message?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  call_results?: CallResultSchema[];
  readiness?: SimpleReadinessInfo;
  /** Rich task context for command center */
  task_summary?: TaskSummarySchema;
  /** Post-workflow evaluation result */
  evaluation_result?: {
    [key: string]: any;
  } | null;
  /** Actions taken by BPO fallback */
  bpo_actions?:
    | {
        [key: string]: any;
      }[]
    | null;
};
export type AutomationHistory = {
  voice_runs?: VoiceAiAgentRunSchema[];
  browser_runs?: ReadBrowserAgentRun[];
};
export type AutomationHistoryRead = {
  voice_runs?: VoiceAiAgentRunSchema[];
  browser_runs?: ReadBrowserAgentRun[];
  /** Count voice runs that actually made calls (excludes runs with calls_made=0). */
  voice_attempts: number;
  /** True if any voice run has received an attestation. */
  got_an_answer_via_voice_agent: boolean;
  portal_attempts: number;
  /** Last voice agent run time (only runs that made calls). */
  last_voice_at: string | null;
  /** Last portal check time. */
  last_portal_at: string | null;
  /** When attestation status last changed. */
  status_changed_at: string | null;
  /** Last update to the police report. */
  updated_at: string | null;
};
export type ReadPoliceReportWithHistory = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
  suggestions?: ReadSuggestion[] | null;
  /** Letters sent to this police department for this matter */
  sent_letters?: ReadPostgridLetter[];
  /** Cheques sent to this police department for this matter */
  sent_cheques?: ReadPostgridCheque[];
  history?: AutomationHistory;
};
export type ReadPoliceReportWithHistoryRead = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
  suggestions?: ReadSuggestion[] | null;
  /** Letters sent to this police department for this matter */
  sent_letters?: ReadPostgridLetter[];
  /** Cheques sent to this police department for this matter */
  sent_cheques?: ReadPostgridCheque[];
  history?: AutomationHistoryRead;
  /** No further automation needed. */
  is_terminal: boolean;
  /** Department confirmed report exists and is available. */
  is_verified_ready: boolean;
  /** Hours since last voice call that made calls, or None if never called with actual calls. */
  hours_since_last_voice: number | null;
  /** Hours since last portal check, or None if never checked. */
  hours_since_last_portal: number | null;
};
export type MatterInvestigationStatus =
  | "need_info"
  | "researching"
  | "investigating"
  | "escalated"
  | "completed_report_received"
  | "closed_no_report"
  | "not_applicable"
  | "backfilled_closed";
export type ServiceSchema = {
  id: string | string;
  name: string;
  display_name: string;
  description: string;
};
export type PoliceReportServiceConfig = {
  service_name?: "police_reports";
};
export type IntakeServiceConfig = {
  service_name?: "intake";
};
export type CaseSetupServiceConfig = {
  service_name?: "case_setup";
};
export type ClientCommunicationServiceConfig = {
  service_name?: "client_communication";
};
export type ClaimsServiceConfig = {
  service_name?: "claims";
};
export type MedicalRecordsServiceConfig = {
  service_name?: "medical_records_and_treatment";
};
export type DemandsServiceConfig = {
  service_name?: "demands";
};
export type LiensServiceConfig = {
  service_name?: "liens";
};
export type FirmServiceSchema = {
  id: string | string;
  firm_id: string | string;
  service_id: string | string;
  service: ServiceSchema;
  notes: string;
  config?:
    | (
        | PoliceReportServiceConfig
        | IntakeServiceConfig
        | CaseSetupServiceConfig
        | ClientCommunicationServiceConfig
        | ClaimsServiceConfig
        | MedicalRecordsServiceConfig
        | DemandsServiceConfig
        | LiensServiceConfig
      )
    | null;
  is_active?: boolean;
};
export type MatterType =
  | "Motor Vehicle Accident"
  | "Medical Malpractice"
  | "Miscellaneous"
  | "Breach of Employment Contract"
  | "Seller Non-Disclosure"
  | "Premises Liability"
  | "Broker Negligence"
  | "Property Damage"
  | "Dog Bite"
  | "Wrongful Death"
  | "Unknown";
export type MatterStatus =
  | "New Lead"
  | "Intake"
  | "Case Ingestion"
  | "Initial Review"
  | "Case Setup"
  | "Treatment"
  | "Treatment Complete"
  | "Ready for Demand"
  | "Demand Preparation"
  | "Demand Letter"
  | "Litigation"
  | "Complete"
  | "Hidden"
  | "In Progress"
  | "Client Dropped"
  | "To Be Dropped";
export type MatterServices =
  | "Screening & Retainer"
  | "Intake Call"
  | "Case Setup"
  | "Medical Records"
  | "Demand";
export type SimplifiedBillingCode =
  | "records_retrieval"
  | "demand_only"
  | "property_damage"
  | "in_flight"
  | "back_office"
  | "full_pre_lit"
  | "case_setup_only"
  | "litigation_only";
export type TaskConfigSchema = {
  gate_tasks_during_initial_review?: boolean;
};
export type ReadMatterSchema = {
  id: string | string;
  name?: string | null;
  matter_type?: MatterType;
  status?: MatterStatus;
  date_of_loss?: string | null;
  statute_of_limitations_date?: string | null;
  services?: MatterServices[];
  city?: string | null;
  state?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
  representing_attorney_id?: (string | string) | null;
  case_manager_id?: (string | string) | null;
  external_cms_url?: string | null;
  cms_email?: string | null;
  referral_source_description?: string | null;
  simplified_billing_code?: SimplifiedBillingCode;
  task_config?: TaskConfigSchema;
};
export type ArtifactType =
  | "TRANSCRIPT"
  | "AUDIO_CONVERSATION"
  | "DOCUMENT"
  | "EMAIL"
  | "EMAIL_ATTACHMENT"
  | "INTAKE_NOTE"
  | "MESSAGE"
  | "MESSAGE_ATTACHMENT"
  | "CONVERSATION"
  | "BLOB"
  | "STAFF_NOTE";
export type IncidentMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
  reasoning?: string | null;
};
export type LocationNormalizationStatus =
  | "pending"
  | "success"
  | "failed"
  | "low_confidence";
export type IncidentLocationSchema = {
  /** Original location from extraction */
  raw_location?: string | null;
  /** Google Maps Place ID */
  place_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formatted_address?: string | null;
  short_formatted_address?: string | null;
  display_name?: string | null;
  street_number?: string | null;
  route?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null;
  state_long?: string | null;
  postal_code?: string | null;
  country?: string | null;
  country_long?: string | null;
  plus_code_global?: string | null;
  plus_code_compound?: string | null;
  primary_type?: string | null;
  types?: string[];
  google_maps_uri?: string | null;
  normalization_confidence?: number | null;
  confidence_reasoning?: string | null;
  normalization_status?: LocationNormalizationStatus;
  satellite_image_url?: string | null;
  /** Street View facing north (0°) */
  streetview_image_url_0?: string | null;
  /** Street View facing east (90°) */
  streetview_image_url_90?: string | null;
  /** Street View facing south (180°) */
  streetview_image_url_180?: string | null;
  /** Street View facing west (270°) */
  streetview_image_url_270?: string | null;
};
export type IncidentSchema = {
  /** Concise, 1-2 sentence summary of the facts of the incident, limited to events that occurred on the date of loss. Involved parties, location, and nature of the incident should be included. */
  description?: string | null;
  date_of_loss?: string | null;
  /** Time of day when the incident occurred, helping establish circumstances and potential contributing factors. */
  time_of_loss?: string | null;
  /** Where the incident took place, formatted for geocoding.
    
    Police reports typically include location in standardized formats - preserve these exactly:
    - Street addresses: "1234 OAK ST" or "1234 Oak Street"
    - Intersections: "MAIN ST / 5TH AVE" or "Main St at 5th Ave"
    - Highway locations: "I-95 NB MM 42.3" or "I-95 Northbound mile marker 42"
    - Relative positions: "100 FT N OF MAIN ST" or "100 feet north of Main St"
    - Reference points: "IFO 123 MAIN ST" (in front of) or "in front of 123 Main St"
    
    When extracting from police reports, look for fields like:
    - "Location of Occurrence", "Crash Location", "Scene Location"
    - "Street/Highway", "At Intersection Of", "Nearest Cross Street"
    - "Feet/Direction From", "Reference Point"
    
    Output format: "[Address/Intersection], [City], [State] [ZIP if available]"
    
    Examples:
    - "1234 Oak Street, Springfield, IL 62701"
    - "Main Street and 5th Avenue, Newark, NJ"
    - "I-95 Northbound mile marker 42, Philadelphia, PA"
    - "Route 1, 100 feet south of Oak Avenue, Edison, NJ"
    - "123 Main St (in front of Walmart), Anytown, NJ"
    
    Include city and state when available. Do not include narrative text. */
  location?: string | null;
  /** Latitude coordinate if explicitly provided in the source (e.g., from a police report or GPS data). Only extract if exact coordinates are stated, do not estimate. */
  extracted_latitude?: number | null;
  /** Longitude coordinate if explicitly provided in the source (e.g., from a police report or GPS data). Only extract if exact coordinates are stated, do not estimate. */
  extracted_longitude?: number | null;
  /** Cross street at incident location, if explicitly stated, otherwise null. */
  cross_street?: string | null;
  /** Environmental conditions at the time of incident that may have contributed to or affected the circumstances. */
  weather_conditions?: string | null;
  /** Factual description of injuries sustained, important for damages assessment and medical records analysis. */
  injuries_described?: string | null;
  /** Category or nature of the incident (vehicle collision, slip and fall, workplace injury, etc.) for case classification and strategy. */
  incident_type?: string | null;
  /** Whether emergency medical care was sought immediately following the incident - relevant for damages and treatment documentation. */
  hospital?: string | null;
  /** Whether emergency medical services responded to the scene - indicates severity and creates potential documentation trail. */
  ambulance?: string | null;
  /** Whether law enforcement documented the incident - critical for obtaining official reports and witness information. */
  police_report?: string | null;
  /** Ongoing medical care status - important for understanding continuing damages and treatment coordination. */
  current_treatment?: string | null;
  /** Presence of witnesses to the incident - essential for case investigation and potential testimony. */
  witnesses?: string | null;
  /** Whether witness statements were obtained - important for preserving testimony and case preparation. */
  statements?: string | null;
  /** Whether photographic or video evidence was captured - crucial for case documentation and demonstrative evidence. */
  photos?: string | null;
  /** Any prior legal representation for this incident - important for conflicts analysis and case coordination. */
  representation?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this incident in the matter, if any.
             */
  mentions?: IncidentMentionStubSchema[] | null;
  /** Normalized location data from Google Maps */
  normalized_location?: IncidentLocationSchema;
};
export type ReadAddress = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
};
export type MatterClientPhoneNumberSchema = {
  phone_number: string;
};
export type ReadMatterClient = {
  id: string | string;
  matter_id: string | string;
  name?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  last_four_of_ssn?: string | null;
  preferred_language?: string | null;
  address?: ReadAddress;
  phone_numbers?: MatterClientPhoneNumberSchema[];
  gender?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  ssn?: string | null;
};
export type MatterSnapshot = {
  id: string | string;
  firm_id?: (string | string) | null;
  matter_status?: string | null;
  status?: MatterInvestigationStatus;
  has_essential_info?: boolean;
  has_parties?: boolean;
  firm_service_configs?: FirmServiceSchema[];
  entries?: ReadPoliceReportWithHistory[];
  investigation_created_at?: string | null;
  investigation_modified_at?: string | null;
  matter?: ReadMatterSchema;
  incident?: IncidentSchema;
  client?: ReadMatterClient;
};
export type MatterSnapshotRead = {
  id: string | string;
  firm_id?: (string | string) | null;
  matter_status?: string | null;
  status?: MatterInvestigationStatus;
  has_essential_info?: boolean;
  has_parties?: boolean;
  firm_service_configs?: FirmServiceSchema[];
  entries?: ReadPoliceReportWithHistoryRead[];
  investigation_created_at?: string | null;
  investigation_modified_at?: string | null;
  matter?: ReadMatterSchema;
  incident?: IncidentSchema;
  client?: ReadMatterClient;
  primary_lead_ids: (string | string)[];
  status_changed_at: string | null;
  investigation_started_at: string | null;
  oldest_attested_entry_created_at: string | null;
  last_progress_at: string | null;
  voice_calls_total: number;
  portal_checks_total: number;
  total_entries_attempted: number;
  days_since_date_of_loss: number | null;
  days_since_matter_created: number | null;
};
export const {
  usePoliceReportsListQuery,
  usePoliceReportsCreateMutation,
  usePoliceReportsRetrieveQuery,
  usePoliceReportsUpdateMutation,
  usePoliceReportsPartialUpdateMutation,
  usePoliceReportsDestroyMutation,
  usePoliceReportsGetBrowserRunsQuery,
  usePoliceReportsStartBrowserAgentMutation,
  usePoliceDepartmentsListBrowserRunsQuery,
  usePoliceReportsEnsureTasksMutation,
  usePoliceDepartmentsFireBrowserRunMutation,
  usePoliceReportsUpdateAttestationStatusMutation,
  usePoliceReportsGetWithAutomationDataQuery,
  usePoliceReportsDiscoverMutation,
  usePoliceReportsMatterSnapshotQuery,
  usePoliceReportsTriggerOrchestrationMutation,
  usePoliceReportsGetOutreachSummaryQuery,
} = injectedRtkApi;
